/**
 * ALTIORA · TemporalBadge — Pill de criticité temporelle.
 *
 * Rend une pastille chromatique (🟢🟡🟠🔴) + libellé humain lisible.
 * Alimenté par `core.temporal_intelligence` (backend) qui expose :
 *   - temporal_criticality      → "can_wait" | "to_plan" | "important_today" | "critical_now"
 *   - temporal_criticality_label → "Peut attendre" | "À planifier" | ...
 *   - temporal_window_label     → "Aujourd'hui" | "Cette semaine" | ...
 *   - time_to_critical_label    → "5 jours" | "3 heures" | null
 *
 * Design : pill fine, très low-key, s'intègre partout sans dominer.
 */
import { Clock } from "lucide-react";

// Palette alignée sur la charte AL­TIORA
const COLORS = {
  can_wait:        { bg: "#0B7A3B18", text: "#0B7A3B", label: "Peut attendre" },
  to_plan:         { bg: "#B4530918", text: "#B45309", label: "À planifier" },
  important_today: { bg: "#C5711018", text: "#C57110", label: "Important aujourd'hui" },
  critical_now:    { bg: "#B91C1C18", text: "#B91C1C", label: "Critique maintenant" },
};

const DEFAULT = { bg: "#94a3b818", text: "#64748B", label: "Sans échéance" };


export default function TemporalBadge({
  criticality,
  criticalityLabel,
  windowLabel,
  timeToCritical,
  size = "sm",
  showIcon = true,
  testid,
}) {
  const c = COLORS[criticality] || DEFAULT;
  const label = criticalityLabel || c.label;
  const isSm = size === "sm";
  const iconSize = isSm ? 10 : 12;
  const textSize = isSm ? "text-[10px]" : "text-[11px]";

  // Composition du libellé secondaire : window OU time-to-critical
  const secondary = timeToCritical || windowLabel;

  return (
    <span
      data-testid={testid || `temporal-badge-${criticality || "none"}`}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-mono uppercase tracking-widest ${textSize}`}
      style={{ background: c.bg, color: c.text, letterSpacing: "0.14em" }}
    >
      {showIcon && <Clock size={iconSize} strokeWidth={2} />}
      <span>{label}</span>
      {secondary && (
        <>
          <span style={{ opacity: 0.55 }}>·</span>
          <span style={{ opacity: 0.85 }}>{secondary}</span>
        </>
      )}
    </span>
  );
}
